import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Socios from './pages/Socios';
import Documentos from './pages/Documentos';
import Fazendas from './pages/Fazendas';
import Agendamentos from './pages/Agendamentos';
import Financeiro from './pages/Financeiro';
import Anuidades from './pages/Anuidades';
import NoticiasSite from './pages/NoticiasSite';
import { branding } from './config/branding';

const theme = createTheme({
  palette: {
    primary: {
      main: branding.colors.primary,
      light: branding.colors.primaryLight,
      dark: branding.colors.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: branding.colors.accent,
    },
    background: {
      default: branding.colors.surface,
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(27, 67, 50, 0.08)',
          border: '1px solid rgba(27, 67, 50, 0.08)',
        },
      },
    },
  },
});

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div>Carregando...</div>;
  }

  const defaultPath = isAuthenticated && !user?.acesso_todos ? '/socios' : '/dashboard';

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />}
      />
      <Route
        path="/reset-password"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <ResetPassword />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Navigate to={defaultPath} replace />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              {user?.acesso_todos ? <Dashboard /> : <Navigate to="/socios" replace />}
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/socios"
        element={
          <ProtectedRoute>
            <Layout>
              <Socios />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentos"
        element={
          <ProtectedRoute>
            <Layout>
              <Documentos />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fazendas"
        element={
          <ProtectedRoute>
            <Layout>
              <Fazendas />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agendamentos"
        element={
          <ProtectedRoute>
            <Layout>
              <Agendamentos />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/financeiro"
        element={
          <ProtectedRoute>
            <Layout>
              <Financeiro />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/anuidades"
        element={
          <ProtectedRoute>
            <Layout>
              <Anuidades />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/noticias-site"
        element={
          <ProtectedRoute>
            <Layout>
              <NoticiasSite />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
